<template>
	<div v-if="ready" class="bg-white border-b border-gray-200">
		<hr class="border-gray-200" />

		<ui-form class="px-4 sm:px-6 lg:px-8 py-6" :loading="loading" @submit="submitBatch">

				<!-- Header: Datum + Preisquelle -->
				<div class="flex flex-col md:flex-row gap-x-6 mb-6">
					<ui-input-group
						class="w-48"
						v-model="form.date"
						field="date"
						label="Datum"
						type="date"
						:form="form"
						:required="true"
						tooltip-text="Wird als Datum für alle Einträge des Batches verwendet"
					/>

					<ui-select-group
						class="w-64"
						v-model="form.price_source_id"
						field="price_source_id"
						label="Preisquelle"
						:options="priceSources"
						:form="form"
						:required="true"
						:placeholder="false"
						tooltip-text="Wird für Vorbelegung zukünftiger Einträge verwendet"
						@change="onPriceSourceChange"
					/>

					<ui-select-group
						v-if="presetOptions.length > 0"
						class="w-64"
						v-model="selectedPresetDate"
						field="preset"
						label="Vorlage"
						:options="presetOptions"
						:form="form"
						:required="false"
						:placeholder="false"
						tooltip-text="Letzte Batches dieser Preisquelle als Startvorlage"
						@change="onPresetChange"
					/>

					<ui-select-group
						v-if="selectedPresetDate && compareOptions.length > 1"
						class="w-64"
						v-model="comparePresetDate"
						field="compare"
						label="Vergleich"
						:options="compareOptions"
						:form="form"
						:required="false"
						:placeholder="false"
						tooltip-text="Zeigt nur die Zeilen, die in der Vorlage, aber noch nicht im Vergleichs-Batch vorkommen"
						@change="onCompareChange"
					/>
				</div>

				<hr class="border-gray-200 mb-4" />

				<!-- Indie-Zeilen -->
				<div class="mb-4">
					<table class="min-w-full divide-y divide-gray-200 text-sm">
						<thead class="bg-gray-50">
							<tr>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{{ __("Quality Grade") }}</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{{ __("Erntejahr") }}</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{{ __("Future") }}</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Parität</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Marktstandort</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Termin</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Von</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Bis</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Matif €</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Modus</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Preis €</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Basis €</th>
								<th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Marge €</th>
								<th class="w-8"></th>
							</tr>
						</thead>

						<tbody class="bg-white divide-y divide-gray-100">
							<tr v-for="(row, idx) in rows" :key="idx">
								<td class="pl-0 pr-3 py-2 align-top min-w-[9rem] max-w-[15rem]">
									<ui-select-group
										class=""
										v-model="row.product_grade_id"
										:field="'rows.' + idx + '.product_grade_id'"
										:options="gradeOptions"
										:form="form"
										:required="true"
										:no-label="true"
										:placeholder="false"
										@change="() => onGradeChange(idx)"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[7rem] max-w-[15rem]">
									<ui-select-group
										class=""
										v-model="row.product_harvest"
										:field="'rows.' + idx + '.product_harvest'"
										:options="harvestYears"
										:form="form"
										:required="true"
										:no-label="true"
										:placeholder="false"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[9rem] max-w-[15rem]">
									<ui-select-group
										class=""
										v-model="row.commodity_future_id"
										:field="'rows.' + idx + '.commodity_future_id'"
										:options="futuresOptionsForRow(idx)"
										:form="form"
										:required="true"
										:no-label="true"
										:placeholder="false"
										@change="() => onFutureChange(idx)"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[7rem] max-w-[15rem]">
									<ui-select-group
										class=""
										v-model="row.location_mode"
										:field="'rows.' + idx + '.location_mode'"
										:options="locationModes"
										:form="form"
										:required="true"
										:no-label="true"
										:placeholder="false"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[8rem] max-w-[15rem]">
									<ui-select-group
										class=""
										v-model="row.market_location_id"
										:field="'rows.' + idx + '.market_location_id'"
										:options="marketLocationOptions"
										:form="form"
										:required="true"
										:no-label="true"
										:placeholder="false"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[9rem] max-w-[15rem]">
									<ui-select-group
										class=""
										v-model="row.date_type"
										:field="'rows.' + idx + '.date_type'"
										:options="dateTypes"
										:form="form"
										:required="true"
										:no-label="true"
										:placeholder="false"
										@change="() => onDateTypeChange(idx)"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[8rem] max-w-[15rem]">
									<ui-month-input-group
										class=""
										v-if="dateMonthFrom(row.date_type)"
										v-model="row.date_month_from"
										:field="'rows.' + idx + '.date_month_from'"
										:form="form"
										:required="true"
										:min-date="monthMinDate"
										:no-label="true"
									/>

									<ui-month-input-group
										class=""
										v-else
										v-model="row.date_month_from"
										:field="'rows.' + idx + '.date_month_from'"
										:form="form"
										:required="false"
										:no-label="true"
										:disabled="true"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[8rem] max-w-[15rem]">
									<ui-month-input-group
										class=""
										v-if="dateMonthTo(row.date_type)"
										v-model="row.date_month_to"
										:field="'rows.' + idx + '.date_month_to'"
										:form="form"
										:required="true"
										:min-date="monthMinDate"
										:no-label="true"
									/>

									<ui-month-input-group
										class=""
										v-else
										v-model="row.date_month_to"
										:field="'rows.' + idx + '.date_month_to'"
										:form="form"
										:required="false"
										:no-label="true"
										:disabled="true"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[7rem] max-w-[15rem]">
									<ui-input-group
										class=""
										v-model="row.matif_kurs"
										:field="'rows.' + idx + '.matif_kurs'"
										:form="form"
										:required="true"
										:no-label="true"
										:dp="2"
										suffix="€"
										@input.native="recalculateRowLive(idx, $event, 'matif')"
									/>
								</td>

								<td class="px-3 py-2 align-top">
									<div class="flex flex-col gap-y-1 pt-1">
										<ui-radio-group
											v-for="mode in inputModes"
											:key="mode.id"
											v-model="row.input_mode"
											:field="'rows.' + idx + '.input_mode'"
											:form="form"
											:name="'rowInputMode_' + idx"
											:radio-value="mode.id"
											:label="mode.name"
										/>
									</div>
								</td>

								<td class="px-3 py-2 align-top min-w-[7rem] max-w-[15rem]">
									<ui-input-group
										class=""
										v-model="row.price_mt"
										:field="'rows.' + idx + '.price_mt'"
										:form="form"
										:required="isPriceInput(row.input_mode)"
										:no-label="true"
										:dp="2"
										suffix="€"
										:disabled="!isPriceInput(row.input_mode)"
										@input.native="recalculateRowLive(idx, $event, 'price_mt')"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[7rem] max-w-[15rem]">
									<ui-input-group
										class=""
										v-model="row.price_basis"
										:field="'rows.' + idx + '.price_basis'"
										:form="form"
										:required="isBasisInput(row.input_mode)"
										:no-label="true"
										:dp="2"
										suffix="€"
										:disabled="!isBasisInput(row.input_mode)"
										@input.native="recalculateRowLive(idx, $event, 'price_basis')"
									/>
								</td>

								<td class="px-3 py-2 align-top min-w-[7rem] max-w-[15rem]">
									<ui-input-group
										class=""
										v-model="row.price_margin"
										:field="'rows.' + idx + '.price_margin'"
										:form="form"
										:required="false"
										:no-label="true"
										:dp="2"
										suffix="€"
										@input.native="recalculateRowLive(idx, $event, 'price_margin')"
									/>
								</td>

								<td class="pl-3 pr-0 py-2 align-middle">
									<button
										type="button"
										class="text-gray-400 hover:text-red-500"
										@click="removeRow(idx)"
									>
										<font-awesome-icon :icon="['fas', 'times']" class="h-4 w-4" />
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Aktionen -->
				<div class="flex items-center justify-between">
					<ui-button type="secondary" @click="addRow">
						<font-awesome-icon :icon="['fas', 'plus']" class="mr-2 h-4 w-4" />
						Zeile hinzufügen
					</ui-button>

					<ui-button
						class="flex items-center flex-shrink-0"
						native-type="submit"
						:disabled="loading || rows.length === 0"
					>
						<font-awesome-icon :icon="['fas', 'plus']" class="mr-2 h-4 w-4" />
						{{ rows.length }} {{ rows.length === 1 ? 'Preis' : 'Preise' }} speichern
					</ui-button>
				</div>

		</ui-form>
	</div>
</template>

<script>
import Vue from "vue";

export default {
	data() {
		return {
			ready:              false,
			loading:            false,
			productTypes:       [],
			productGrades:      [],
			locationModes:      [],
			inputModes:         [],
			defaultInputMode:   null,
			dateTypes:          [],
			marketLocations:    [],
			priceSources:       [],
			activeFutures:      [],
			harvestYears:       [],
			currentHarvestYear: null,
			presets:            [],
			selectedPresetDate: null,
			comparePresetDate:  null,
			rows:               [],
			form: Vue.observable({
				errors:       {},
				hasErrors:    false,
				clearErrors() {},
				date:             new Date().toISOString().slice(0, 10),
				price_source_id:  null,
			}),
		};
	},

	created() {
		this.loadDependencies();
	},

	computed: {
		gradeOptions() {
			return this.productGrades.map((g) => {
				const raw              = g.name.replace("product.grades.long.", "");
				const [grade, protein] = raw.split("__");
				const label            = this.__("product.grades.default." + grade);

				return {
					id:   g.id,
					name: protein ? `${label} (${this.__("product.proteins." + protein)})` : label,
				};
			});
		},

		marketLocationOptions() {
			return this.marketLocations.map((l) => ({
				id:   l.id,
				name: l.label,
			}));
		},

		/**
		 * Auswählbare Vorlagen (letzte Batches der Preisquelle), neuester zuerst.
		 *
		 * @return {Array}
		 */
		presetOptions() {
			return this.presets.map((p) => ({
				id:   p.date,
				name: `${this.formatPresetDate(p.date)} (${p.count} ${p.count === 1 ? 'Eintrag' : 'Einträge'})`,
			}));
		},

		/**
		 * Vergleichs-Optionen: die Vorlagen ohne die aktuell gewählte Vorlage, plus eine
		 * Leeroption zum Deaktivieren des Vergleichs.
		 *
		 * @return {Array}
		 */
		compareOptions() {
			return [
				{ id: null, name: '— kein Vergleich —' },
				...this.presetOptions.filter((o) => o.id !== this.selectedPresetDate),
			];
		},

		/**
		 * Frühester wählbarer Liefermonat: der Monat des Batch-Datums (form.date). Verhindert die
		 * Auswahl von Monaten aus der Vergangenheit relativ zum Eingabedatum.
		 *
		 * @return {string} ISO "YYYY-MM-01"
		 */
		monthMinDate() {
			const date = this.form.date || new Date().toISOString().slice(0, 10);

			return date.slice(0, 7) + "-01";
		},
	},

	methods: {
		/**
		 * Lädt die Select-Optionen und Futures der Batch-Erfassung und legt die erste Zeile an.
		 *
		 * @return {void}
		 */
		async loadDependencies() {
			try {
				const response = await axios.get(this.$route("admin.prices.batch.dependencies"));

				const deps = response.data;

				this.productTypes       = deps.productTypes;
				this.productGrades      = deps.productGrades;
				this.locationModes      = deps.locationModes;
				this.inputModes         = deps.inputModes;
				this.defaultInputMode   = deps.defaultInputMode;
				this.dateTypes          = deps.dateTypes;
				this.marketLocations    = deps.marketLocations;
				this.priceSources       = deps.priceSources;
				this.activeFutures      = deps.activeFutures;
				this.harvestYears       = deps.harvestYears;
				this.currentHarvestYear = deps.currentHarvestYear;

				this.rows  = [this.emptyRow()];
				this.ready = true;
			} catch (e) {
				this.$bus.$emit("toast", { type: "error", message: this.__("Could not load the batch entry.") });
			}
		},

		/**
		 * @param {string|null} modeId
		 * @return {boolean}
		 */
		isPriceInput(modeId) {
			return this.inputModes.find((m) => m.id === modeId)?.is_price_input === true;
		},

		/**
		 * @param {string|null} modeId
		 * @return {boolean}
		 */
		isBasisInput(modeId) {
			return this.inputModes.find((m) => m.id === modeId)?.is_basis_input === true;
		},

		/**
		 * @param {string|null} dateType
		 * @return {boolean}
		 */
		dateMonthFrom(dateType) {
			return this.dateTypes.find((t) => t.id === dateType)?.date_month_from ?? false;
		},

		/**
		 * @param {string|null} dateType
		 * @return {boolean}
		 */
		dateMonthTo(dateType) {
			return this.dateTypes.find((t) => t.id === dateType)?.date_month_to ?? false;
		},

		/**
		 * @return {Object}
		 */
		emptyRow() {
			return {
				product_grade_id:    null,
				product_harvest:     this.currentHarvestYear,
				commodity_future_id: null,
				market_location_id:  null,
				location_mode:       null,
				date_type:           null,
				date_month_from:     null,
				date_month_to:       null,
				matif_kurs:          null,
				input_mode:          this.defaultInputMode,
				price_mt:            null,
				price_basis:         null,
				price_margin:        null,
			};
		},

		/**
		 * @param {number} idx
		 * @return {Array}
		 */
		futuresOptionsForRow(idx) {
			const gradeId = this.rows[idx]?.product_grade_id;
			const grade   = this.productGrades.find((g) => g.id === gradeId);

			if (!grade) return [];

			const ticker = this.productTypes.find((t) => t.id === grade.product_type_id)?.ticker_code;

			return (this.activeFutures || [])
				.filter((f) => f.ticker_code === ticker)
				.map((f) => ({ id: f.id, name: f.name }));
		},

		/**
		 * @param {number} idx
		 * @return {void}
		 */
		onDateTypeChange(idx) {
			this.rows[idx].date_month_from = null;
			this.rows[idx].date_month_to   = null;
		},

		/**
		 * @param {number} idx
		 * @return {void}
		 */
		onGradeChange(idx) {
			this.rows[idx].commodity_future_id = null;
		},

		/**
		 * @param {number} idx
		 * @return {void}
		 */
		onFutureChange(idx) {
			const future = (this.activeFutures || []).find((f) => f.id === this.rows[idx].commodity_future_id);

			if (!future) return;

			this.rows[idx].matif_kurs = future.euronext_price;
			this.recalculateRow(idx);
		},

		/**
		 * @return {void}
		 */
		addRow() {
			const last = this.rows[this.rows.length - 1];
			this.rows.push({
				...this.emptyRow(),
				product_grade_id:   last?.product_grade_id   ?? null,
				product_harvest:    last?.product_harvest    ?? this.currentHarvestYear,
				commodity_future_id: last?.commodity_future_id ?? null,
				market_location_id: last?.market_location_id ?? null,
				location_mode:      last?.location_mode      ?? null,
				date_type:          last?.date_type          ?? null,
				date_month_from:    this.dateMonthFrom(last?.date_type) ? (last?.date_month_from ?? null) : null,
				date_month_to:      last?.date_month_to      ?? null,
				input_mode:         last?.input_mode         ?? this.defaultInputMode,
				price_margin:       last?.price_margin       ?? null,
			});

			// matif_kurs am neu übernommenen Future hängt am euronext_price und wird
			// nur über onFutureChange gesetzt — @change feuert bei programmatischer
			// Vorbelegung nicht, daher hier manuell auslösen.
			this.onFutureChange(this.rows.length - 1);
		},

		/**
		 * @param {number} idx
		 * @return {void}
		 */
		removeRow(idx) {
			this.rows.splice(idx, 1);
		},

		/**
		 * @param {number} idx
		 * @return {void}
		 */
		recalculateRow(idx) {
			const row    = this.rows[idx];
			const matif  = parseFloat(row.matif_kurs);
			const margin = parseFloat(row.price_margin) || 0;

			if (this.isPriceInput(row.input_mode)) {
				const priceMt   = parseFloat(row.price_mt);
				row.price_basis = (!isNaN(matif) && !isNaN(priceMt)) ? (priceMt - margin - matif).toFixed(2) : null;
			} else {
				const priceBasis = parseFloat(row.price_basis);
				row.price_mt     = (!isNaN(matif) && !isNaN(priceBasis)) ? (matif + priceBasis + margin).toFixed(2) : null;
			}
		},

		/**
		 * Real-time recalculation while the user is typing (native input event).
		 * Reads the live value from the event, the committed value for the other field from the row.
		 *
		 * @param {number} idx
		 * @param {InputEvent} event
		 * @param {string} field - 'matif', 'price_mt' or 'price_basis'
		 * @return {void}
		 */
		recalculateRowLive(idx, event, field) {
			const parse = (v) => parseFloat(('' + (v ?? '')).replace(',', '.'));

			const live   = parse(event.target.value);
			const matif  = field === 'matif' ? live : parse(this.rows[idx].matif_kurs);
			const margin = (field === 'price_margin' ? live : parse(this.rows[idx].price_margin)) || 0;

			// Marge wirkt nur auf den Preis; Basis bleibt unberührt → bei Margenänderung den Preis neu rechnen.
			if (field === 'price_margin') {
				const priceBasis        = parse(this.rows[idx].price_basis);
				this.rows[idx].price_mt = (!isNaN(matif) && !isNaN(priceBasis)) ? (matif + priceBasis + margin).toFixed(2) : null;

				return;
			}

			if (this.isPriceInput(this.rows[idx].input_mode)) {
				const priceMt              = field === 'price_mt' ? live : parse(this.rows[idx].price_mt);
				this.rows[idx].price_basis = (!isNaN(matif) && !isNaN(priceMt)) ? (priceMt - margin - matif).toFixed(2) : null;
			} else {
				const priceBasis           = field === 'price_basis' ? live : parse(this.rows[idx].price_basis);
				this.rows[idx].price_mt    = (!isNaN(matif) && !isNaN(priceBasis)) ? (matif + priceBasis + margin).toFixed(2) : null;
			}
		},

		/**
		 * 'YYYY-MM-DD' → 'DD.MM.YYYY' für die Vorlagen-Beschriftung.
		 *
		 * @param {string} date
		 * @return {string}
		 */
		formatPresetDate(date) {
			const [year, month, day] = date.split('-');

			return `${day}.${month}.${year}`;
		},

		/**
		 * Beim Wechsel der Preisquelle die bestehenden Validierungsfehler zurücksetzen
		 * (leeres form:invalid cleart alle Felder) und die Vorlagen neu laden.
		 *
		 * @return {void}
		 */
		onPriceSourceChange() {
			this.$bus.$emit('form:invalid', {});
			this.presets            = [];
			this.selectedPresetDate = null;
			this.comparePresetDate  = null;
			this.loadPresets();
		},

		/**
		 * Beim Wechsel der Vorlage die Fehler zurücksetzen und die Auswahl neu anwenden.
		 * Der Vergleich darf nicht die Vorlage selbst sein.
		 *
		 * @return {void}
		 */
		onPresetChange() {
			this.$bus.$emit('form:invalid', {});

			if (this.comparePresetDate === this.selectedPresetDate) {
				this.comparePresetDate = null;
			}

			this.applySelection();
		},

		/**
		 * Beim Wechsel des Vergleichs die Fehler zurücksetzen und die Auswahl neu anwenden.
		 *
		 * @return {void}
		 */
		onCompareChange() {
			this.$bus.$emit('form:invalid', {});
			this.applySelection();
		},

		/**
		 * Lädt die letzten Batches der gewählten Preisquelle als Vorlagen und wendet die
		 * jüngste automatisch an.
		 *
		 * @return {void}
		 */
		async loadPresets() {
			if (!this.form.price_source_id) return;

			try {
				const response = await axios.get(
					this.$route('admin.prices.recent_by_source', { priceSourceId: this.form.price_source_id })
				);

				this.presets = response.data.data || [];

				if (this.presets.length === 0) return;

				this.selectedPresetDate = this.presets[0].date;
				this.comparePresetDate  = null;
				this.applySelection();
			} catch (e) {
				// Keine Vorlage vorhanden — Felder bleiben leer
			}
		},

		/**
		 * Vergleichsschlüssel eines Eintrags: identifiziert den Preis-Punkt ohne Datum/Quelle/Werte
		 * (Marktstandort + Güte + Erntejahr + Parität + Liefertermin). Dient dem Batch-Vergleich,
		 * um bereits erfasste Zeilen herauszufiltern. NULL-Monate als '' normalisiert.
		 *
		 * @param {Object} row
		 * @return {string}
		 */
		compareKey(row) {
			return [
				row.market_location_id,
				row.product_grade_id,
				row.product_harvest,
				row.location_mode,
				row.date_type,
				row.date_month_from ?? '',
				row.date_month_to ?? '',
			].join('|');
		},

		/**
		 * Wendet die Auswahl auf das Formular an: die Zeilen der Vorlage, optional reduziert um alle,
		 * die im Vergleichs-Batch bereits vorkommen (→ nur die noch fehlenden Nachzügler).
		 *
		 * Append-Modus (Vorlage == Batch des eingestellten Datums): gespeicherte Werte und Modus unverändert
		 * laden (inkl. Matif zum Eintragezeitpunkt). Sonst (anderer Tag = Neuanlage): Matif frisch aus dem
		 * Future, der im gespeicherten Modus eingegebene Wert bleibt, der andere wird neu gerechnet.
		 * Zeilen ohne gespeicherten Modus werden aus der Basis gerechnet, der Modus bleibt leer.
		 *
		 * @return {void}
		 */
		applySelection() {
			const base = this.presets.find((p) => p.date === this.selectedPresetDate);

			if (!base) return;

			let rows = base.rows;

			const compare = this.comparePresetDate
				? this.presets.find((p) => p.date === this.comparePresetDate)
				: null;

			if (compare) {
				const existing = new Set(compare.rows.map((r) => this.compareKey(r)));
				rows = base.rows.filter((r) => !existing.has(this.compareKey(r)));
			}

			const append = base.date === this.form.date;

			this.rows = rows.map((entry) => this.rowFromPresetEntry(entry, append));
		},

		/**
		 * Baut eine Formularzeile aus einem Vorlagen-Eintrag.
		 *
		 * @param {Object} entry
		 * @param {boolean} append - true = gespeicherte Werte beibehalten, false = Matif/Preis frisch rechnen
		 * @return {Object}
		 */
		rowFromPresetEntry(entry, append) {
			const future = (this.activeFutures || []).find((f) => f.id === entry.commodity_future_id);
			const margin = entry.price_margin;

			// Gespeicherter Modus; Altbestand ohne Modus bleibt unausgewählt (kein Backfill).
			const mode = entry.input_mode;

			let matif;
			let priceMt;
			let basis;

			if (append) {
				matif   = entry.matif_kurs;
				priceMt = entry.price_mt;
				basis   = entry.price_basis;
			} else {
				matif = future ? future.euronext_price : null;

				const matifNum  = parseFloat(matif);
				const marginNum = parseFloat(margin) || 0;

				if (this.isPriceInput(mode)) {
					const priceNum = parseFloat(entry.price_mt);
					priceMt = entry.price_mt;
					basis   = (!isNaN(matifNum) && !isNaN(priceNum)) ? (priceNum - marginNum - matifNum).toFixed(2) : null;
				} else {
					// Basis-Modus oder unbekannt: aus der Basis neu rechnen.
					const basisNum = parseFloat(entry.price_basis);
					basis   = entry.price_basis;
					priceMt = (!isNaN(matifNum) && !isNaN(basisNum)) ? (matifNum + basisNum + marginNum).toFixed(2) : null;
				}
			}

			return {
				...this.emptyRow(),
				product_grade_id:    entry.product_grade_id,
				product_harvest:     entry.product_harvest ?? this.currentHarvestYear,
				commodity_future_id: entry.commodity_future_id,
				market_location_id:  entry.market_location_id,
				location_mode:       entry.location_mode,
				date_type:           entry.date_type,
				date_month_from:     this.dateMonthFrom(entry.date_type) ? entry.date_month_from : null,
				date_month_to:       entry.date_month_to,
				matif_kurs:          matif,
				input_mode:          mode,
				price_basis:         basis,
				price_margin:        margin,
				price_mt:            priceMt,
			};
		},

		/**
		 * @return {void}
		 */
		async submitBatch() {
			this.loading = true;

			const rows = this.rows.map((row) => ({
				product_grade_id:    row.product_grade_id,
				product_harvest:     row.product_harvest,
				commodity_future_id: row.commodity_future_id,
				market_location_id:  row.market_location_id,
				location_mode:       row.location_mode,
				date_type:           row.date_type,
				date_month_from:     row.date_month_from,
				date_month_to:       row.date_month_to,
				matif_kurs:          row.matif_kurs,
				input_mode:          row.input_mode,
				price_mt:            row.price_mt,
				price_basis:         row.price_basis,
				price_margin:        row.price_margin,
			}));

			try {
				const response = await axios.post(this.$route('admin.prices.batch'), {
					date:            this.form.date,
					price_source_id: this.form.price_source_id,
					rows,
				});

				const created = response.data.created ?? 0;
				const updated = response.data.updated ?? 0;
				const message = updated > 0
					? `${created} Preise erstellt, ${updated} aktualisiert.`
					: `${created} ${created === 1 ? 'Preis' : 'Preise'} erstellt.`;

				this.$bus.$emit('content:load');
				this.$bus.$emit('toast', { type: 'success', message });

				this.form.date            = new Date().toISOString().slice(0, 10);
				this.form.price_source_id = null;
				this.presets              = [];
				this.selectedPresetDate   = null;
				this.comparePresetDate    = null;

				// Matif/Preis/Basis/Marge leeren, Struktur und Vorbelegung behalten
				this.rows = this.rows.map((row) => ({
					...row,
					matif_kurs:   null,
					price_mt:     null,
					price_basis:  null,
					price_margin: null,
				}));
			} catch (error) {
				if (error.response?.status === 422) {
					this.$bus.$emit('form:invalid', error.response.data.errors);
				} else {
					this.$bus.$emit('modal:error', error);
				}
			} finally {
				this.loading = false;
			}
		},
	},
};
</script>
